import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pin, RefreshCw, X, MessageSquare, Sparkles, Users, Share2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { API } from "@/context/AuthContext";
import { AiChart, renderMarkdown } from "@/components/AiAnswer";

const ago = (iso) => {
  if (!iso) return "";
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const h = Math.round(mins / 60);
  return h < 24 ? `${h} h ago` : `${Math.round(h / 24)} d ago`;
};

function Tile({ pin, onRefresh, onUnpin, onShare, busy, isAdmin }) {
  let spec = null;
  if (pin.kind === "chart") {
    try { spec = JSON.parse(pin.content); } catch { spec = null; }
  }
  const ask = () => window.dispatchEvent(new CustomEvent("open-ai-agent", { detail: { question: pin.question } }));
  return (
    <Card className="border-slate-200 shadow-sm" data-testid={`pinned-tile-${pin.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800 truncate" title={pin.title} data-testid={`pinned-title-${pin.id}`}>{pin.title}</p>
            <p className="text-[11px] text-slate-400">
              {pin.shared && <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 text-sky-700 px-1.5 py-0.5 font-semibold mr-1.5" data-testid={`pinned-shared-badge-${pin.id}`}><Users className="h-2.5 w-2.5" />Shared by {pin.shared_by}</span>}
              as of {ago(pin.rendered_at)}{pin.stale && !busy ? " · refreshing…" : ""}{pin.error ? <span className="text-red-500"> · last refresh failed</span> : null}
            </p>
          </div>
          <div className="flex gap-1 shrink-0">
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={ask} title="Ask again in the AI Analyst" data-testid={`pinned-ask-${pin.id}`}><MessageSquare className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onRefresh(pin.id)} disabled={busy} title="Refresh with live data" data-testid={`pinned-refresh-${pin.id}`}><RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /></Button>
            {isAdmin && (
              <Button size="sm" variant="ghost" className={`h-7 w-7 p-0 ${pin.shared ? "text-sky-700" : "text-slate-400"}`} onClick={() => onShare(pin.id, !pin.shared)} title={pin.shared ? "Stop sharing with team" : "Share with team"} data-testid={`pinned-share-${pin.id}`}><Share2 className="h-3.5 w-3.5" /></Button>
            )}
            {pin.can_manage && <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600" onClick={() => onUnpin(pin.id)} title="Unpin" data-testid={`pinned-unpin-${pin.id}`}><X className="h-3.5 w-3.5" /></Button>}
          </div>
        </div>
        {pin.kind === "chart"
          ? (spec ? <AiChart spec={{ ...spec, title: undefined }} idx={pin.id} /> : <p className="text-xs text-slate-400">Chart unavailable</p>)
          : <div className="ai-markdown text-sm max-h-72 overflow-auto" dangerouslySetInnerHTML={renderMarkdown((pin.content || "").replace(/^\s*\*\*[^\n]*\*\*\s*\n+/, ""))} />}
      </CardContent>
    </Card>
  );
}

export function PinnedInsights() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [pins, setPins] = useState(null);
  const [busy, setBusy] = useState(() => new Set());

  const refresh = useCallback(async (id) => {
    setBusy((b) => new Set(b).add(id));
    try {
      const r = await axios.post(`${API}/ai/pins/${id}/refresh`);
      setPins((ps) => (ps || []).map((p) => (p.id === id ? r.data : p)));
      if (r.data.error) toast.error(`Could not refresh "${r.data.title}"`);
    } catch { toast.error("Refresh failed"); } finally {
      setBusy((b) => { const n = new Set(b); n.delete(id); return n; });
    }
  }, []);

  useEffect(() => {
    axios.get(`${API}/ai/pins`).then((r) => {
      setPins(r.data);
      r.data.filter((p) => p.stale).forEach((p) => refresh(p.id));
    }).catch(() => setPins([]));
  }, [refresh]);

  const unpin = async (id) => {
    try {
      await axios.delete(`${API}/ai/pins/${id}`);
      setPins((ps) => ps.filter((p) => p.id !== id));
      toast.success("Unpinned");
    } catch { toast.error("Could not unpin"); }
  };

  const share = async (id, shared) => {
    try {
      const r = await axios.post(`${API}/ai/pins/${id}/share`, { shared });
      setPins((ps) => ps.map((p) => (p.id === id ? r.data : p)));
      toast.success(shared ? "Shared with the whole team" : "No longer shared");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update sharing"); }
  };

  if (!pins || pins.length === 0) return null;
  const team = pins.filter((p) => p.shared && !p.own);
  const mine = pins.filter((p) => p.own);
  const grid = (list) => (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {list.map((p) => <Tile key={p.id} pin={p} onRefresh={refresh} onUnpin={unpin} onShare={share} busy={busy.has(p.id)} isAdmin={isAdmin} />)}
    </div>
  );
  return (
    <section className="space-y-3 fade-in-up" data-testid="pinned-insights">
      {team.length > 0 && (
        <div className="space-y-3" data-testid="team-insights">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-sky-700" />
            <h2 className="text-base md:text-lg font-semibold text-slate-800">Team insights</h2>
            <span className="text-xs text-slate-400">shared by admins · one common refresh for everyone</span>
          </div>
          {grid(team)}
        </div>
      )}
      {mine.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Pin className="h-4 w-4 text-sky-700" />
            <h2 className="text-base md:text-lg font-semibold text-slate-800">My pinned insights</h2>
            <span className="text-xs text-slate-400 inline-flex items-center gap-1"><Sparkles className="h-3 w-3" />re-generated from live data · cached 1 h · {mine.length}/6{isAdmin ? " · use the share icon to publish a tile to the team" : ""}</span>
          </div>
          {grid(mine)}
        </div>
      )}
    </section>
  );
}
